<template>
  <div class="term-of-use-section">
    <b-card style="max-width: 34rem;">
      <b-card-title class="dark-variant">Terms of Use</b-card-title>

      <div class="tou-meta">
        <span class="tou-badge">Effective 2 September 2020</span>
        <span class="tou-entity">Amejman LLC, DBA AmejPay</span>
      </div>

      <b-card-text class="tou-lead">
        These terms apply automatically when you download or use Amejpay, so please
        read them carefully before you continue using the app.
      </b-card-text>

      <div class="term-of-use-info">
        <div class="tou-body">
          <section
            class="tou-sec"
            v-for="(section, index) in sections"
            :key="'tou-' + index"
          >
            <h6 class="tou-sec-title">{{ index + 1 }}. {{ section.title }}</h6>
            <p
              class="tou-para"
              v-for="(para, pIndex) in section.paragraphs"
              :key="'tou-' + index + '-' + pIndex"
            >{{ para }}</p>
            <ul class="tou-list" v-if="section.links && section.links.length">
              <li v-for="(link, lIndex) in section.links" :key="'tou-l-' + lIndex">
                <a :href="link.url" target="_blank" rel="noopener noreferrer">{{ link.label }}</a>
              </li>
            </ul>
          </section>
        </div>
      </div>

      <div class="tou-foot">
        <p class="light-variant">
          We may update these terms from time to time. Any change is published on this
          page, so please review it periodically.
        </p>
        <div class="tou-links">
          <router-link :to="{ name: 'termAndCondition' }" target="_blank">
            Full Terms &amp; Conditions
          </router-link>
          <span class="tou-sep">|</span>
          <router-link :to="{ name: 'pricacyPolicy' }" target="_blank">
            Privacy Policy
          </router-link>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
export default {
  name: "TermofUseSection",
  data() {
    return {
      sections: [
        {
          title: "Your licence to use the app",
          paragraphs: [
            "You are not allowed to copy or modify the app, any part of the app, or our trademarks in any way. You are not allowed to attempt to extract the source code of the app, and you should not try to translate the app into other languages or make derivative versions.",
            "The app itself, and all the trademarks, copyright, database rights and other intellectual property rights related to it, still belong to Amejman LLC, DBA AmejPay."
          ]
        },
        {
          title: "Changes and charges",
          paragraphs: [
            "AmejPay is committed to ensuring that the app is as useful and efficient as possible. For that reason we reserve the right to make changes to the app, or to charge for its services, at any time and for any reason.",
            "We will never charge you for the app or its services without making it very clear to you exactly what you are paying for."
          ]
        },
        {
          title: "Your data and device security",
          paragraphs: [
            "The Amejpay app stores and processes personal data that you have provided to us in order to provide our service. It is your responsibility to keep your phone and your access to the app secure.",
            "We therefore recommend that you do not jailbreak or root your phone. Removing the software restrictions imposed by the official operating system could make your phone vulnerable to malware, viruses and malicious programs, compromise your phone's security features, and could mean that the Amejpay app will not work properly or at all."
          ]
        },
        {
          title: "Third party services",
          paragraphs: [
            "The app does use third party services that declare their own terms and conditions."
          ],
          links: [
            { label: "Google Play Services", url: "https://policies.google.com/terms" },
            { label: "Google Analytics for Firebase", url: "https://firebase.google.com/terms/analytics" },
            { label: "Firebase Crashlytics", url: "https://firebase.google.com/terms/crashlytics" }
          ]
        },
        {
          title: "Connectivity and network charges",
          paragraphs: [
            "Certain functions of the app require an active internet connection. The connection can be Wi-Fi or provided by your mobile network provider, but Amejman LLC, DBA AmejPay cannot take responsibility for the app not working at full functionality if you do not have access to Wi-Fi or you do not have any of your data allowance left.",
            "If you are using the app outside of an area with Wi-Fi, the terms of the agreement with your mobile network provider still apply. You may be charged by your mobile provider for the cost of data for the duration of the connection, or other third party charges. In using the app you accept responsibility for any such charges, including roaming data charges if you use the app outside your home territory without turning off data roaming.",
            "If you are not the bill payer for the device on which you are using the app, we assume that you have received permission from the bill payer."
          ]
        },
        {
          title: "Limits of our responsibility",
          paragraphs: [
            "Amejman LLC, DBA AmejPay cannot always take responsibility for the way you use the app. For example, you need to make sure that your device stays charged - if it runs out of battery and you cannot turn it on to use the service, we cannot accept responsibility.",
            "Although we endeavour to ensure that the app is updated and correct at all times, we do rely on third parties to provide information to us so that we can make it available to you. Amejman LLC, DBA AmejPay accepts no liability for any loss, direct or indirect, that you experience as a result of relying wholly on this functionality of the app."
          ]
        },
        {
          title: "Updates and termination",
          paragraphs: [
            "At some point we may wish to update the app. The app is currently available on Android and iOS, and the requirements for both systems may change, so you will need to download the updates if you want to keep using the app. Amejman LLC, DBA AmejPay does not promise that it will always update the app so that it is relevant to you, or that it works with the Android and iOS version installed on your device. You promise to always accept updates to the application when offered to you.",
            "We may also wish to stop providing the app, and may terminate use of it at any time without giving notice of termination to you. Unless we tell you otherwise, upon any termination the rights and licences granted to you in these terms will end, and you must stop using the app and, if needed, delete it from your device."
          ]
        }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
@import "../../css/style.scss";

.term-of-use-section {
  .tou-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .tou-badge {
    display: inline-block;
    background: #eef4fb;
    color: #0066b3;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 600;
    margin-right: 10px;
  }

  .tou-entity {
    font-size: 12px;
    color: #8a94a6;
  }

  .tou-lead {
    font-size: 14px;
  }

  .term-of-use-info {
    display: block;
  }

  .tou-body {
    max-height: 460px;
    overflow-y: auto;
    padding-right: 10px;
    border-top: 1px solid #f0f2f5;
    border-bottom: 1px solid #f0f2f5;
  }

  .tou-sec {
    padding: 16px 0;
    border-bottom: 1px solid #f5f7fa;
  }

  .tou-sec:last-child {
    border-bottom: 0;
  }

  .tou-sec-title {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
  }

  .tou-para {
    font-size: 13px;
    line-height: 1.6;
    color: #6b7280;
    margin-bottom: 10px;
  }

  .tou-para:last-of-type {
    margin-bottom: 0;
  }

  .tou-list {
    margin: 8px 0 0 18px;
    padding: 0;
    font-size: 13px;

    li {
      margin-bottom: 4px;
    }
  }

  .tou-foot {
    padding-top: 14px;

    p {
      font-size: 12px;
      margin-bottom: 8px;
    }
  }

  .tou-links {
    font-size: 13px;

    .tou-sep {
      color: #cbd2dc;
      margin: 0 8px;
    }
  }
}
</style>
